import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { searchContents } from '../lib/api'
import { cn, contentTypeConfig } from '../lib/utils'
import {
  Search,
  X,
  FileText,
  Image,
  Link,
  File,
  BookOpen,
  Mic,
  Clock,
  ArrowRight,
  Command,
  Loader2,
  Sparkles,
  Globe,
  Hash,
  FolderOpen,
  Zap,
} from 'lucide-react'

const contentTypeIcons = {
  note: FileText,
  image: Image,
  bookmark: Globe,
  pdf: File,
  epub: BookOpen,
  audio: Mic,
  transcript: Mic,
  txt: FileText,
  default: File,
}

const quickActions = [
  { icon: Sparkles, label: 'Ask AI', description: 'Chat with your knowledge', path: '/chat', color: '#6366f1' },
  { icon: FileText, label: 'New Note', description: 'Create a markdown note', path: '/kb', color: '#FF6B6B' },
  { icon: FolderOpen, label: 'Workspace', description: 'Browse all content', path: '/kb', color: '#4ECDC4' },
]

export default function GlobalSearch({ isOpen, onClose }) {
  const navigate = useNavigate()
  const inputRef = useRef(null)
  const resultsRef = useRef(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [recentSearches, setRecentSearches] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sigmanote_recent_searches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to parse recent searches:', e)
      }
    }
  }, [])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
      setQuery('')
      setResults([])
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (isOpen) {
          onClose()
        }
      }

      if (!isOpen) return

      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        const maxIndex = query ? results.length - 1 : quickActions.length - 1
        setSelectedIndex((prev) => Math.min(prev + 1, maxIndex))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (query && results.length > 0) {
          handleSelect(results[selectedIndex])
        } else if (!query && quickActions[selectedIndex]) {
          navigate(quickActions[selectedIndex].path)
          onClose()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, results, selectedIndex, query, navigate])

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`)
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex])

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([])
      return
    }

    setLoading(true)
    try {
      const response = await searchContents(searchQuery)
      console.log('GlobalSearch response:', response)
      // API returns { results: { matches: [...], count: ... }, metadata: {...} }
      // Each match has: { id, score, metadata: { content_id, title, user_id, chunk_id } }
      let matches = []
      if (response.results?.matches && Array.isArray(response.results.matches)) {
        // Transform matches to flatten metadata for UI display
        matches = response.results.matches.map(match => ({
          content_id: match.metadata?.content_id || match.id,
          title: match.metadata?.title || 'Untitled',
          type: match.metadata?.type || 'unknown',
          score: match.score,
          chunk_id: match.metadata?.chunk_id,
          chunk: match.metadata?.chunk || match.content,
          space_name: match.metadata?.space_name,
          description: match.description,
          content: match.content,
          updated_at: match.updated_at,
          ...match.metadata,
          ...match, // Spread enriched fields
        }))
      } else if (Array.isArray(response.results)) {
        matches = response.results
      } else if (Array.isArray(response.contents)) {
        matches = response.contents
      }
      console.log('GlobalSearch processed matches:', matches.length, matches)
      setResults(matches)
      setSelectedIndex(0)
    } catch (error) {
      console.error('Search failed:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query)
    }, 300)

    return () => clearTimeout(timer)
  }, [query, performSearch])

  const handleSelect = (result) => {
    // Save to recent searches
    const newRecent = [
      { query, timestamp: Date.now() },
      ...recentSearches.filter((r) => r.query !== query),
    ].slice(0, 5)
    setRecentSearches(newRecent)
    localStorage.setItem('sigmanote_recent_searches', JSON.stringify(newRecent))

    // Navigate to content
    navigate(`/content/${result.content_id}`)
    onClose()
  }

  const handleRecentClick = (recentQuery) => {
    setQuery(recentQuery)
  }

  const clearRecentSearches = () => {
    setRecentSearches([])
    localStorage.removeItem('sigmanote_recent_searches')
  }

  const getIcon = (type) => {
    return contentTypeIcons[type] || contentTypeIcons.default
  }

  const getTypeColor = (type) => {
    const config = contentTypeConfig[type] || contentTypeConfig.unknown
    return config.color
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Search Modal */}
      <div className="fixed inset-x-4 top-[15%] sm:top-[20%] max-w-2xl mx-auto z-50 animate-scale-in">
        <div className="glass-card rounded-3xl shadow-2xl overflow-hidden border-border/50">
          {/* Search Input */}
          <div className="flex items-center gap-4 px-5 py-4 border-b border-border/50">
            <div className="relative">
              <Search className="w-5 h-5 text-muted-foreground" />
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your knowledge base..."
              className="flex-1 bg-transparent text-lg outline-none placeholder:text-muted-foreground"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1.5 rounded-lg hover:bg-accent transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-muted border border-border text-xs text-muted-foreground">
              esc
            </kbd>
          </div>

          {/* Results Area */}
          <div ref={resultsRef} className="max-h-[60vh] overflow-y-auto">
            {/* Quick Actions (when no query) */}
            {!query && (
              <div className="p-3">
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Zap className="w-3 h-3" />
                  Quick Actions
                </div>
                <div className="space-y-1">
                  {quickActions.map((action, index) => (
                    <button
                      key={action.label}
                      data-index={index}
                      onClick={() => {
                        navigate(action.path)
                        onClose()
                      }}
                      className={cn(
                        "w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-all",
                        selectedIndex === index
                          ? "bg-primary/10 shadow-sm"
                          : "hover:bg-accent/50"
                      )}
                    >
                      <div
                        className="p-2.5 rounded-xl"
                        style={{ backgroundColor: `${action.color}15` }}
                      >
                        <action.icon className="w-5 h-5" style={{ color: action.color }} />
                      </div>
                      <div className="flex-1">
                        <p className={cn(
                          "font-medium",
                          selectedIndex === index && "text-primary"
                        )}>{action.label}</p>
                        <p className="text-xs text-muted-foreground">{action.description}</p>
                      </div>
                      {selectedIndex === index && (
                        <kbd className="px-2 py-1 rounded-lg bg-primary/10 text-xs text-primary">
                          ↵
                        </kbd>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Searches */}
            {!query && recentSearches.length > 0 && (
              <div className="p-3 border-t border-border/50">
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Recent Searches
                  </span>
                  <button
                    onClick={clearRecentSearches}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <div className="space-y-1">
                  {recentSearches.map((recent, index) => (
                    <button
                      key={index}
                      onClick={() => handleRecentClick(recent.query)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-accent/50 text-left transition-colors"
                    >
                      <Hash className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1 truncate text-sm">{recent.query}</span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {query && results.length > 0 && (
              <div className="p-3">
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Search className="w-3 h-3" />
                  Results
                  <span className="text-muted-foreground/70">({results.length})</span>
                </div>
                <div className="space-y-1">
                  {results.map((result, index) => {
                    const Icon = getIcon(result.type)
                    const color = getTypeColor(result.type)
                    return (
                      <button
                        key={result.content_id}
                        data-index={index}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          "w-full flex items-start gap-4 px-4 py-3 rounded-xl text-left transition-all",
                          selectedIndex === index
                            ? "bg-primary/10 shadow-sm"
                            : "hover:bg-accent/50"
                        )}
                      >
                        <div
                          className="p-2.5 rounded-xl mt-0.5 flex-shrink-0"
                          style={{ backgroundColor: `${color}15` }}
                        >
                          <Icon className="w-4 h-4" style={{ color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "font-medium truncate",
                            selectedIndex === index && "text-primary"
                          )}>{result.title}</p>
                          {result.chunk && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                              {result.chunk}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{ backgroundColor: `${color}15`, color }}
                            >
                              {result.type}
                            </span>
                            {result.space_name && (
                              <>
                                <span className="text-xs text-muted-foreground">in</span>
                                <span className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                  <FolderOpen className="w-3 h-3" />
                                  {result.space_name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {selectedIndex === index && (
                          <kbd className="hidden sm:flex items-center px-2 py-1 rounded-lg bg-primary/10 text-xs text-primary flex-shrink-0 mt-1">
                            ↵
                          </kbd>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* No Results */}
            {query && !loading && results.length === 0 && (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-muted-foreground/50" />
                </div>
                <p className="text-foreground font-medium mb-1">No results found</p>
                <p className="text-sm text-muted-foreground">
                  No matches for "<span className="text-foreground">{query}</span>"
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Try different keywords or check your spelling
                </p>
              </div>
            )}

            {/* Empty State (no query, no recent) */}
            {!query && recentSearches.length === 0 && (
              <div className="p-8 text-center border-t border-border/50">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <p className="text-foreground font-medium mb-1">Search your knowledge</p>
                <p className="text-sm text-muted-foreground">
                  Find notes, bookmarks, documents, and more
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-border/50 bg-muted/30 text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <kbd className="kbd">↑</kbd>
                <kbd className="kbd">↓</kbd>
                <span className="hidden sm:inline">navigate</span>
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="kbd">↵</kbd>
                <span className="hidden sm:inline">select</span>
              </span>
            </div>
            <span className="flex items-center gap-1.5">
              <kbd className="kbd flex items-center gap-0.5">
                <Command className="w-3 h-3" />K
              </kbd>
              <span className="hidden sm:inline">toggle</span>
            </span>
          </div>
        </div>
      </div>
    </>
  )
}
